// ===== 多设备实时同步引擎（核心）=====
// 架构：离线优先 + 快照 diff（快照即 outbox）+ 拉取先行 + 记录级 LWW。
//
// 设计要点（改动前必读）：
//  1. 拉取先行：每轮同步必须 pull 成功后才 push —— 防止离线设备把旧基线数据
//     当成新变更推上去，覆盖其他设备的新编辑（致命场景，不可放宽）。
//  2. 快照即 outbox：snap 记录每个 domain/id 上次成功推送的哈希；推送失败快照
//     不更新，下轮触发自动重算重推，天然断点续传，无需独立队列。
//  3. 防回环：applyingRemote 标志——拉取应用（dbSet/setItem）触发的写入通知
//     被忽略，且应用时同步更新 snap，避免把自己刚拉的数据再推回去。
//  4. updatedAt 取「域写入时刻」（写入钩子记录），而非推送时刻——尽量保住
//     离线编辑的先后关系；时钟偏差由「服务器锚定时钟」（clockOffset，#766）+
//     客户端 meta 过滤 LWW 兜底（服务器只追加不裁决）。
//  5. 域分两类：IndexedDB 核心域（经 db.ts 写入钩子）+ localStorage 业务域
//     （白名单复用 backupService，Storage.prototype 拦截）。
//  6. 数组且每项均有稳定 id → 记录级 diff；否则整值单记录（id='__value__'）。
//     记录消失 → 墓碑（deleted:true）。
//  7. 加密锁定时（isEncryptionEnabled && !hasSessionKey）跳过本轮同步：
//     dbGet 读不到敏感域，diff 会误判全量删除，必须防护。

import { KEYS, dbGet, dbSet, dbDel, onDbWrite } from './db';
import { isEncryptionEnabled, hasSessionKey } from './crypto';
import { syncPull, syncPush, isSettledPushStatus, type SyncRecord } from './syncApi';
import { isLocalOnlySyncDomain } from './syncAuth';
import { loadSession } from './authApi';
import { shouldResetSyncForAccount } from './syncAccountBoundary';
import { setMockCustomers, setMockSuppliers, setMockProducts, setMockSales, repairProductBrandSupplierNow, repairSupplierBrandCategoryNow } from '@/data/mockData';
import { BUSINESS_DATA_KEYS, SETTINGS_KEYS } from '@/services/backupService';
import { shouldQueueBusySync, shouldRunRejectedReconcile, SYNC_INTERVAL_MS } from '@/lib/syncScheduling';
import {
  getSyncConfig,
  type SyncConfig,
} from './syncConfig';
import type { Customer, Supplier, Product, SalesOrder } from '@/types';

// ---------- 配置 ----------

const DEVICE_ID_KEY = 'changfa_device_id';
export const SYNC_APPLIED_EVENT = 'changfa:sync-applied';
export { ensureSyncConfig, getSyncConfig, saveSyncConfig } from './syncConfig';
export type { SyncConfig } from './syncConfig';

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = (crypto.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// ---------- 域定义 ----------

interface DomainDef {
  domain: string;
  storage: 'idb' | 'ls';
  key: string;
  /** sync 模式：'record' = 每条记录一条 sync_record（适合低频改动）；'value' = 整份数据一条 sync_record（适合高频小改动的列表型数据，避免 #762 / #830 products 域 4 万条 record / 21.6MB 撑爆服务端）。 */
  mode?: 'record' | 'value';
  applyToMemory?: (value: unknown) => void;
}

const IDB_DOMAINS: DomainDef[] = [
  { domain: 'customers', storage: 'idb', key: KEYS.customers, applyToMemory: (v) => setMockCustomers(v as Customer[]) },
  { domain: 'suppliers', storage: 'idb', key: KEYS.suppliers, applyToMemory: (v) => setMockSuppliers(v as Supplier[]) },
  // #A2 products 域改为整值模式：商品数据每次新增/改价/改库存都新增一条 record 撑爆 sync_records（实测 21.6MB / 40376 条），
  //  改成「整份 product 数组作一条 record + LWW」：每次改动只推 1 条 ~1-3MB 的整值 record，远低于 4 万条累计的体积和拉取开销。
  //  副作用：①其他设备拉取会"整份覆盖"产品库，所以必须确保 products 在多设备上是「最终一致」而非「局部精确」——已确认是用户接受语义；
  //  ②业务层本地修改 product 时也会触发整份推送（已有防抖 3s）；
  //  ③历史上已写入的 40376 条旧 record 会在下次 syncNow 拉取时由服务端 "value 模式覆盖" 自动废弃（applyRemoteRecords 走整值分支会清掉旧记录型）。
  { domain: 'products', storage: 'idb', key: KEYS.products, mode: 'value', applyToMemory: (v) => setMockProducts(v as Product[]) },
  { domain: 'salesOrders', storage: 'idb', key: KEYS.salesOrders, applyToMemory: (v) => setMockSales(v as SalesOrder[]) },
  { domain: 'warehouses', storage: 'idb', key: KEYS.warehouses },
  { domain: 'trash', storage: 'idb', key: KEYS.trash },
];

const LS_DOMAINS: DomainDef[] = [...BUSINESS_DATA_KEYS, ...SETTINGS_KEYS]
  .filter((k) => !isLocalOnlySyncDomain(k))
  .map((k) => ({
  domain: k,
  storage: 'ls' as const,
  key: k,
}));

const ALL_DOMAINS = [...IDB_DOMAINS, ...LS_DOMAINS];
const DOMAIN_BY_KEY = new Map(ALL_DOMAINS.map((d) => [`${d.storage}:${d.key}`, d]));

/** 拉取应用后需要派发的既有页面级事件（保持与本地写入一致的通知体验） */
const LS_KEY_EVENTS: Record<string, string> = {
  changfa_todos_v1: 'changfa:todos-changed',
  changfa_custom_festivals: 'changfa-festivals-changed',
  changfa_favorite_addresses: 'changfa:favorites-changed', // #751 地图收藏地址同步后通知 MapPage 刷新
  changfa_map_marker_preferences: 'changfa:map-marker-preferences-changed',
  changfa_store_location: 'changfa:store-location-changed',
  // #767 新纳入同步的键：拉取应用后通知持有本地 state 的页面重载
  changfa_exchange_records: 'changfa:exchange-changed',     // 换货记录 → SalesPage
  changfa_returns: 'changfa:returns-changed',               // 退货记录 → SalesPage
  changfa_permission_matrix: 'changfa:permissions-changed',  // 权限矩阵 → SystemPage
  changfa_driver_locations: 'changfa:driver-locations-changed', // #789 司机位置 → MapPage 司机模式刷新
  // 注：网格打印模板 / 个人默认打印模板 / 云打印设置均为「按需读取」（每次打开
  // 页面/执行打印时直接读 localStorage），同步落盘后自然生效，无需页面级事件。
};

// ---------- 引擎状态 ----------

interface SyncState {
  cursor: number;
  /** meta[domain][id] = 已知服务端该记录的 updatedAt（拉取/推送成功后更新） */
  meta: Record<string, Record<string, number>>;
  /** snap[domain][id] = 本地该记录上次成功同步的哈希（记录级） */
  snap: Record<string, Record<string, number>>;
  /**
   * #A12（2026-09-13）value-mode 全局接管 seq 跟踪：
   *  valueTakeoverSeq[domain] = 本机最近一次成功推送该域 __value__ 整值 record 时的服务端 seq。
   *  用途：computeChanges 推送整值前查这个字段，如果本机已接管过且 snap[VALUE_ID]===h（数据未变），
   *  就跳过推送 — 避免「所有设备都重新跑一次 products 整值推送」（实测 history 中曾出现
   *  A→B→A→B 的反复推送，每次都因为 repairProductBrandSupplier 修改数据导致 hash 变化）。
   *  兜底：服务端 handlePull 响应头 X-Products-Takeover-Seq 也会携带全局接管 seq，
   *  客户端拉取后写入本字段 → 即便本机从未推过 __value__，也能跳过重复推送。
   */
  valueTakeoverSeq: Record<string, number>;
}

const STATE_KEY = 'sync_state_v1';
/** #A15 服务端强制全量重同步版本号（与服务端 fullResyncVersion 比对） */
const FULL_RESYNC_KEY = 'changfa_full_resync_v';
const SYNC_ACCOUNT_KEY = 'changfa_sync_account_v1';
const VALUE_ID = '__value__';
const FAVORITE_ADDRESS_DOMAIN = 'changfa_favorite_addresses';

let state: SyncState = { cursor: 0, meta: {}, snap: {}, valueTakeoverSeq: {} };
let started = false;
let syncing = false;
// #831 看门狗用：记录 syncing 进入 true 的时刻，visibilitychange 触发时判断是否卡死
let syncingSince = 0;
let pendingAfterSync = false;
let lastRejectedReconcileAt = 0;
let applyingRemote = false;
let writeTimes: Record<string, number> = {};
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastSyncAt = 0;
let lastError = '';
// 账号变化后的第一轮必须仅从服务器重建本地快照，不能把上一个账号的旧缓存回推。
let skipPushOnce = false;
// #766 时钟偏差：LWW 时间戳全网锚定服务器时钟。每次 pull 用响应里的 serverTime
// 修正 offset；computeChanges 生成的 updatedAt = Date.now() + clockOffset，
// 消除设备时钟快/慢导致的「旧数据覆盖新数据」。
let clockOffset = 0;
let listeners: Array<() => void> = [];export interface SyncStatus {
  enabled: boolean;
  configured: boolean;
  syncing: boolean;
  lastSyncAt: number;
  lastError: string;
  cursor: number;
  deviceId: string;
  deviceName: string;
  serverUrl: string;
}

export function getSyncStatus(): SyncStatus {
  const cfg = getSyncConfig();
  return {
    enabled: !!cfg?.enabled,
    configured: !!cfg,
    syncing,
    lastSyncAt,
    lastError,
    cursor: state.cursor,
    deviceId: getDeviceId(),
    deviceName: cfg?.deviceName || '',
    serverUrl: cfg?.serverUrl || '',
  };
}

export function onSyncStatusChange(fn: () => void): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter((l) => l !== fn); };
}

/**
 * 登录账号变化时建立新的同步边界：保留本地缓存作为可覆盖的显示缓存，
 * 但丢弃旧账号的 cursor/meta/snap，下一轮从服务器完整恢复且不回推旧缓存。
 */
export async function prepareSyncForAccount(username: string): Promise<boolean> {
  const previousUsername = localStorage.getItem(SYNC_ACCOUNT_KEY);
  const changed = shouldResetSyncForAccount(previousUsername, username);
  localStorage.setItem(SYNC_ACCOUNT_KEY, username);
  if (!changed) return false;

  if (syncing && !(await waitForSyncIdle(8_000))) {
    throw new Error('上一账号的同步尚未完成，请稍后重新登录，避免遗漏未上传数据');
  }
  // 账号之间绝不复用业务缓存。仅归零 cursor/snap 仍会遗留新账号无权读取的记录，
  // 下一轮又可能把它们以新令牌推回服务器，既泄漏也污染共享数据。
  await clearAccountScopedCache();
  state = { cursor: 0, meta: {}, snap: {}, valueTakeoverSeq: {} };
  skipPushOnce = true;
  await persistState();
  return true;
}

/** 清除可同步业务缓存；设备标识、登录态和本地专属设置都不在 ALL_DOMAINS 内，保持不动。 */
async function clearAccountScopedCache(): Promise<void> {
  applyingRemote = true;
  try {
    await Promise.all(ALL_DOMAINS.map((domain) => writeDomain(domain, null)));
    for (const domain of IDB_DOMAINS) domain.applyToMemory?.([]);
  } finally {
    applyingRemote = false;
  }
}

function emitStatus(): void {
  for (const fn of listeners) { try { fn(); } catch { /* Best-effort operation; failure is non-fatal. */ } }
}

// ---------- 哈希（cyrb53：快速非加密，仅用于变更检测）----------

function hashOf(v: unknown): number {
  const s = typeof v === 'string' ? v : JSON.stringify(v) ?? 'null';
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** 数组是否为「记录型」（每项均有稳定 id） */
function isRecordArray(v: unknown): v is Array<Record<string, unknown>> {
  return (
    Array.isArray(v) && v.length > 0 &&
    v.every((x) => x && typeof x === 'object' && !Array.isArray(x) &&
      (x as { id?: unknown }).id != null &&
      (typeof (x as { id?: unknown }).id === 'string' || typeof (x as { id?: unknown }).id === 'number'))
  );
}

function recId(x: { id: string | number }): string { return String(x.id); }

// ---------- 域读写 ----------

async function readDomain(d: DomainDef): Promise<unknown> {
  if (d.storage === 'idb') return dbGet<unknown>(d.key);
  try {
    const raw = localStorage.getItem(d.key);
    return raw == null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

async function writeDomain(d: DomainDef, value: unknown): Promise<void> {
  applyingRemote = true;
  try {
    if (d.storage === 'idb') {
      if (value === null) await dbDel(d.key);
      else await dbSet(d.key, value);
    } else {
      if (value === null) localStorage.removeItem(d.key);
      else localStorage.setItem(d.key, JSON.stringify(value));
    }
  } finally {
    applyingRemote = false;
  }
}

// ---------- 变更计算（diff：当前状态 vs 快照）----------

/** 加密锁定（读不到敏感域）时不能 diff，直接放弃本轮 */
function encryptionLocked(): boolean {
  try { return isEncryptionEnabled() && !hasSessionKey(); } catch { return false; }
}

export interface DiffResult {
  records: SyncRecord[];
  touched: string[];
}

export async function computeChanges(deviceId: string): Promise<DiffResult> {
  const out: SyncRecord[] = [];
  const touched: string[] = [];
  // #766：时间戳用服务器锚定时钟（writeTimes 在写入钩子里已加过 offset）
  const updatedAt = Date.now() + clockOffset;

  for (const d of ALL_DOMAINS) {
    const cur = await readDomain(d);
    const snap = state.snap[d.domain] || {};
    const now = writeTimes[d.domain] || updatedAt;

    // 读不到且历史上有内容：可能是加密锁定（IndexedDB 敏感域）→ 跳过该域，防止误判全删
    if (cur === null && Object.keys(snap).length > 0 && d.storage === 'idb') continue;

    // 核心 IndexedDB 域（customers/products/...）：按声明的 mode 走分支。
    //  · 记录级模式（默认）：每条记录一条 sync_record（适合低频改动）。
    //  · 整值模式（products 等高频小改动的列表）：整份数据作一条 sync_record，
    //    节省 sync_records 体积（#830 products 21.6MB → ~1-3MB）。
    if (d.storage === 'idb') {
      // 整值模式：直接把整份数组当一条 record 推（LWW）。空库也照推 __value__:[]（其他设备拉取会拿到空数组覆盖）。
      // ⚠️ 注释里"空库设备把整键清空推上去"的风险在 value 模式下是用户明确接受的语义；
      //   业务上 customers/salesOrders/warehouses/trash 仍保持记录级，不会被这条规则误伤。
      if (d.mode === 'value') {
        const value = Array.isArray(cur) ? cur : [];
        const h = hashOf(value);
        // #A12（2026-09-13）value-mode 整值推送去重：
        //  · 本机已接管过（state.valueTakeoverSeq[d.domain] !== undefined）且
        //  · snap[VALUE_ID] === h（数据未变）
        //  → 跳过本次推送，避免「A 推 __value__ → B 拉 → B 推 __value__ → A 拉 → A 再推」循环。
        //  · 显式检查而非只靠 snap[VALUE_ID] === h：因为 repairProductBrandSupplier 等修复函数
        //  会改动数据导致 hash 漂移，单看 hash 不可靠；valueTakeoverSeq 是「本机曾接管」+「服务端确认接受」
        //  的双门控，更稳。
        const takeoverSeq = state.valueTakeoverSeq[d.domain] || 0;
        const alreadyTakenOver = takeoverSeq > 0;
        if (alreadyTakenOver && snap[VALUE_ID] === h) continue;
        // 整值模式下 snap 只用 VALUE_ID 一个键；如果残留了旧记录级 snap 项，先发墓碑清干净
        // （否则服务端会同时存在"id=具体商品"+"id=__value__"两类 record，apply 侧判定不可预期）。
        const staleRecordIds = Object.keys(snap).filter(id => id !== VALUE_ID);
        for (const staleId of staleRecordIds) {
          out.push({ domain: d.domain, id: staleId, data: null, deleted: true, updatedAt: now, deviceId });
        }
        if (snap[VALUE_ID] !== h || staleRecordIds.length > 0 || !alreadyTakenOver) {
          out.push({ domain: d.domain, id: VALUE_ID, data: value, deleted: false, updatedAt: now, deviceId });
          touched.push(d.domain);
        }
        continue;
      }

      const arr = Array.isArray(cur) ? (cur as Array<Record<string, unknown>>) : [];
      const curMap = new Map<string, number>();
      for (const item of arr) curMap.set(recId(item as { id: string | number }), hashOf(item));
      let changed = false;
      for (const [id, item] of arr.map((it) => [recId(it as { id: string | number }), it] as const)) {
        if (snap[id] !== curMap.get(id)) {
          out.push({ domain: d.domain, id, data: item, deleted: false, updatedAt: now, deviceId });
          changed = true;
        }
      }
      for (const id of Object.keys(snap)) {
        if (id !== VALUE_ID && !curMap.has(id)) {
          out.push({ domain: d.domain, id, data: null, deleted: true, updatedAt: now, deviceId });
          changed = true;
        }
      }
      if (snap[VALUE_ID] !== undefined) {
        out.push({ domain: d.domain, id: VALUE_ID, data: null, deleted: true, updatedAt: now, deviceId });
        changed = true;
      }
      if (changed) touched.push(d.domain);
      continue;
    }

    // 空数组同样是「记录集为空」，绝不能落进整值分支 —— 否则 __value__:[] 会
    // 整键覆盖其他设备上已有的记录（#756 脏数据来源之一：seq 5118/5123）
    if (Array.isArray(cur) && (cur.length === 0 || isRecordArray(cur))) {
      const curMap = new Map<string, { h: number; item: Record<string, unknown> }>();
      for (const item of cur) {
        const id = recId(item as { id: string | number });
        curMap.set(id, { h: hashOf(item), item });
      }
      let changed = false;
      // 记录级：新增/修改
      for (const [id, { h, item }] of curMap) {
        if (snap[id] !== h) {
          out.push({ domain: d.domain, id, data: item, deleted: false, updatedAt: now, deviceId });
          changed = true;
        }
      }
      // 记录级：删除 → 墓碑
      for (const id of Object.keys(snap)) {
        if (id !== VALUE_ID && !curMap.has(id)) {
          out.push({ domain: d.domain, id, data: null, deleted: true, updatedAt: now, deviceId });
          changed = true;
        }
      }
      // 原为整值模式 → 已被记录型数组取代：只清本地快照标记，不再发整值墓碑。
      // （整值墓碑在其他设备拉取应用时会把整键清空 —— #756 根因；
      //  服务器上遗留的旧整值记录由 apply 侧守卫兜底，新设备重放时按记录级合并）
      if (snap[VALUE_ID] !== undefined) delete snap[VALUE_ID];
      if (changed) touched.push(d.domain);
    } else if (cur !== null) {
      // 整值模式：整键 LWW
      const h = hashOf(cur);
      const snapKeys = Object.keys(snap);
      const isValueMode = snapKeys.length === 0 || (snapKeys.length === 1 && snapKeys[0] === VALUE_ID);
      if (snap[VALUE_ID] !== h || !isValueMode) {
        // 原为记录模式 → 补齐旧记录的墓碑，避免服务端同时存在记录与整值两种形态
        for (const id of snapKeys) {
          if (id !== VALUE_ID) {
            out.push({ domain: d.domain, id, data: null, deleted: true, updatedAt: now, deviceId });
          }
        }
        out.push({ domain: d.domain, id: VALUE_ID, data: cur, deleted: false, updatedAt: now, deviceId });
        touched.push(d.domain);
      }
    } else {
      // cur === null(本地读不到):通常发生在 LS 域 race condition
      //  (applyRemoteRecords 还没把 server 数据写到 LS,computeChanges 已经先跑)
      //  或用户主动 removeItem 把 key 清空。
      // 风险:之前这里无脑推 tombstone,2026-09-14 凌晨 admin002 设备被自动 race
      //  触发了 fav 12 个 tombstone(整批永久删除)。
      // 修复:LS 域一律不推 tombstone,等下一次同步让 applyRemoteRecords 先写 LS
      //  → computeChanges 再算 diff;真正的"用户主动清空"场景应在 UI 显式
      //  removeAll,由它把 snap 清掉走 isRecordArray 空数组分支(line 316)。
      if (d.storage === 'ls') continue;
      // 当前为 null(IDB 域才走到这里,line 254 之前已 continue 过 race):
      // 历史所有记录 → 墓碑
      let changed = false;
      for (const id of Object.keys(snap)) {
        out.push({ domain: d.domain, id, data: null, deleted: true, updatedAt: now, deviceId });
        changed = true;
      }
      if (changed) touched.push(d.domain);
    }
  }
  return { records: out, touched };
}

// ---------- 拉取应用 ----------

function upsertById(list: unknown, id: string, item: unknown): unknown[] {
  const arr = Array.isArray(list) ? list : [];
  const idx = arr.findIndex((x) => x && typeof x === 'object' && String((x as { id?: unknown }).id) === id);
  if (idx >= 0) { const next = [...arr]; next[idx] = item; return next; }
  return [...arr, item];
}

function removeById(list: unknown, id: string): unknown[] {
  const arr = Array.isArray(list) ? list : [];
  return arr.filter((x) => !(x && typeof x === 'object' && String((x as { id?: unknown }).id) === id));
}

/** 应用一批远端记录（已按 seq 升序）。返回实际应用条数与涉及域。 */
async function applyRemoteRecords(records: SyncRecord[]): Promise<{ applied: number; domains: string[] }> {
  const domainSet = new Set<string>();
  let applied = 0;

  // 按 domain 分组，减少整域读写次数
  const byDomain = new Map<string, SyncRecord[]>();
  for (const r of records) {
    // 服务端已停止下发旧版整份地址簿；旧服务残留或缓存重放时，客户端也绝不允许它覆盖单条地址记录。
    if (r.domain === FAVORITE_ADDRESS_DOMAIN && r.id === VALUE_ID) continue;
    if (!byDomain.has(r.domain)) byDomain.set(r.domain, []);
    byDomain.get(r.domain)!.push(r);
  }

  const defByDomain = new Map(ALL_DOMAINS.map((d) => [d.domain, d]));

  for (const [domain, recs] of byDomain) {
    const def = defByDomain.get(domain);
    if (!def) continue; // 未知域（版本不一致）忽略
    const meta = (state.meta[domain] ||= {});
    // #766：服务器 updated_at 列为 VARCHAR(64)，拉回是字符串；位数不同的字符串
    // 关系比较会错序（"999" > "1000"），比较/记账前一律显式数值化
    const tsOf = (r: SyncRecord): number => Number(r.updatedAt) || 0;

    // 逐条按 LWW 过滤（meta = 已知服务端状态的 updatedAt）
    const needApply = recs.filter((r) => {
      const known = meta[r.id];
      return known === undefined || tsOf(r) > known;
    });
    if (needApply.length === 0) {
      // 无需应用也更新 meta，防止同一记录反复比较
      for (const r of recs) meta[r.id] = tsOf(r);
      continue;
    }

    const cur = await readDomain(def);
    const snap = (state.snap[domain] ||= {});
    let nextVal: unknown = cur;

    // #765b 写序修正：snap/meta 变更先收集到操作队列，writeDomain 成功落盘后
    // 才提交。原实现就地修改 + 异常时 persistState 仍保存，会出现「已记账但
    // 未落盘」→ 该记录被 meta 过滤永久跳过。cursor 由调用方在 apply 成功后才
    // 推进，落盘失败下一轮重放同批记录（已应用过的域被 meta 幂等跳过）。
    const metaOps: Array<[string, number]> = [];
    const snapDel: string[] = [];
    const snapSet: Array<[string, number]> = [];
    let wipeSnap = false; // 整值覆盖 / 整键删除：清空整个 snap

    for (const r of needApply) {
      const ts = tsOf(r);
      if (r.id === VALUE_ID) {
        // 整值模式：仅声明 mode === 'value' 的域允许整键替换（#A2 products 等高频列表）；
        //  其他 IDB 域（customers/salesOrders/warehouses/trash）即使收到整值记录（历史脏数据/防御）
        //  标记已处理后忽略，防止整键清空（#756 根因）。
        if (def.storage === 'idb' && def.mode !== 'value') { metaOps.push([r.id, ts]); continue; }
        // 守卫 1：整值携带空数组 = 历史脏数据（空数组曾被误当整值推送，如 seq 5118/5123），
        // 直接跳过，绝不整键覆盖其他设备上已有的记录
        if (!r.deleted && Array.isArray(r.data) && r.data.length === 0) {
          metaOps.push([r.id, ts]);
          continue;
        }
        if (r.deleted) {
          // 守卫 2：本地仍是「非空记录型数组」→ 记录级数据活着（历史模式迁移墓碑，
          // 如 seq 5122/7521；或本地还有离线未推送的编辑），只清整值标记、绝不清键。
          // 真正的整键删除场景中，记录级墓碑会先把数组清空，走到这里时必然为空 → 正常清键
          const aliveRecords = Array.isArray(nextVal) && nextVal.length > 0 &&
            nextVal.every((x) => x && typeof x === 'object' && (x as { id?: unknown }).id != null);
          if (aliveRecords) {
            snapDel.push(VALUE_ID);
            metaOps.push([r.id, ts]);
            continue;
          }
          nextVal = null;
          wipeSnap = true;
        } else {
          nextVal = r.data;
          wipeSnap = true;
          snapSet.push([VALUE_ID, hashOf(r.data)]);
        }
      } else {
        // 记录级：确保载体是数组（空/不存在时从 [] 开始建）
        if (r.deleted) {
          nextVal = removeById(nextVal, r.id);
          snapDel.push(r.id);
        } else {
          nextVal = upsertById(nextVal, r.id, r.data);
          snapSet.push([r.id, hashOf(r.data)]);
        }
      }
      metaOps.push([r.id, ts]);
      applied++;
    }

    // 落盘成功后才提交 snap/meta（#765b）
    await writeDomain(def, nextVal);
    if (wipeSnap) for (const k of Object.keys(snap)) delete snap[k];
    for (const id of snapDel) delete snap[id];
    for (const [id, h] of snapSet) snap[id] = h;
    for (const [id, ts] of metaOps) meta[id] = ts;
    if (def.applyToMemory && nextVal !== null && nextVal !== undefined) def.applyToMemory(nextVal);
    domainSet.add(domain);
  }

  // 页面级通知（沿用既有事件名 + 通用同步事件）
  if (domainSet.size > 0) {
    for (const [key, ev] of Object.entries(LS_KEY_EVENTS)) {
      if (domainSet.has(key)) window.dispatchEvent(new Event(ev));
    }
    window.dispatchEvent(new CustomEvent(SYNC_APPLIED_EVENT, { detail: { domains: [...domainSet] } }));
  }
  return { applied, domains: [...domainSet] };
}

// ---------- 同步主循环 ----------

export interface SyncRejectedRecord {
  domain: string;
  id: string;
  status: string;
  reason?: string;
}

interface PushAllResult {
  accepted: number;
  rejected: SyncRejectedRecord[];
}

/** 分批推送（按条数切片；服务端 MAX_BODY 32MB，正常增量远达不到） */
async function pushAll(cfg: SyncConfig, deviceId: string, records: SyncRecord[]): Promise<PushAllResult> {
  const BATCH = 300;
  let accepted = 0;
  const rejected: SyncRejectedRecord[] = [];
  for (let i = 0; i < records.length; i += BATCH) {
    const batch = records.slice(i, i + BATCH);
    const res = await syncPush(cfg, deviceId, batch);
    // 只有 accepted 才能确认本地快照。被拒绝的版本必须留待服务器完整回填，
    // 不能把它标记成“已经同步”，否则会永久停留在本机。
    for (let j = 0; j < batch.length; j++) {
      const r = batch[j];
      const item = res.results[j];
      const st = item?.status;
      if (!isSettledPushStatus(st)) {
        throw new Error(`服务器返回未知同步结果：${st || '缺失'}`);
      }
      if (st === 'accepted') {
        accepted++;
        const snap = (state.snap[r.domain] ||= {});
        if (r.deleted) delete snap[r.id];
        else snap[r.id] = hashOf(r.data);
        const meta = (state.meta[r.domain] ||= {});
        meta[r.id] = Number(r.updatedAt) || 0;
        // #A12 跟踪本机最近一次成功推送 value mode 整值 record 的服务端 seq。
        //  computeChanges 用这个字段 + snap[VALUE_ID] 一起判断是否再推 __value__，
        //  避免「所有设备都重新跑一次 products 整值推送」循环。
        if (r.id === VALUE_ID) {
          const acceptedSeq = item.seq;
          if (typeof acceptedSeq === 'number' && acceptedSeq > 0) {
            const cur = state.valueTakeoverSeq[r.domain] || 0;
            if (acceptedSeq > cur) state.valueTakeoverSeq[r.domain] = acceptedSeq;
          }
        }
      } else {
        rejected.push({ domain: r.domain, id: r.id, status: st, reason: item.reason });
      }
    }
  }
  return { accepted, rejected };
}

async function persistState(): Promise<void> {
  applyingRemote = true;
  try { await dbSet(STATE_KEY, state); } catch { /* Best-effort operation; failure is non-fatal. */ } finally { applyingRemote = false; }
}

export interface SyncNowResult {
  ok: boolean;
  pulled: number;
  pushed: number;
  rejected?: SyncRejectedRecord[];
  error?: string;
}

/** 等待正在进行的一轮同步结束；退出前必须先让队列清空，避免令牌失效后还有待上传的数据。 */
function waitForSyncIdle(timeoutMs: number): Promise<boolean> {
  if (!syncing) return Promise.resolve(true);
  return new Promise((resolve) => {
    let unsubscribe = () => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, timeoutMs);
    unsubscribe = onSyncStatusChange(() => {
      if (!syncing) {
        clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });
}

/**
 * 在注销前完成一轮同步。若已有同步在执行，先等待其结束，再用仍有效的登录令牌补做一轮。
 * 调用方只有在 ok 时才可撤销服务器令牌。
 */
export async function flushSyncBeforeLogout(): Promise<SyncNowResult> {
  // #A16（2026-09-14）超时 30s → 8s。
  //  原实现在 iPhone 上「点退出后转圈 30 秒然后提示失败、实际没退出」，用户体验极差。
  //  退出只会 clearSession（清 token），**不会清本地业务数据**，未上传的改动仍在本机，
  //  下次登录会自动继续同步 —— 因此长时间阻塞退出没有任何收益，反而制造「卡死」错觉。
  const deadline = Date.now() + 8_000;
  for (;;) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return { ok: false, pulled: 0, pushed: 0, error: '等待同步超时' };

    if (syncing) {
      const idle = await waitForSyncIdle(remaining);
      if (!idle) return { ok: false, pulled: 0, pushed: 0, error: '等待同步超时' };
    }

    const result = await syncNow('logout-flush');
    if (result.error !== '同步进行中') return result;
  }
}

/** 地址收藏保存后要求服务器确认该条记录，避免界面显示已保存、重新登录后却找不到。
 *  修复路线（#A1 自检）：本地 hash 通过并不能代表服务端真的收到了——push 可能因
 *  网络抖动/服务端物化器异常返回 accepted 但实际未持久化。
 *
 *  #830 重写：不再走 flushSyncBeforeLogout 全量同步（iPhone 上 4 万条全量拉取要 30-90s，
 *  30s 兜底超时返回 false 直接导致保存"显示成功实际失败"）。改为：
 *    1) 直接对 (domain, id) 强推一条 + 等待响应
 *    2) hasRecordOnServer 二次校验（避免"accepted 但未持久化"）
 *    3) 任一失败才 fallback 到全量 syncNow
 */
export async function syncFavoriteAddressNow(favoriteId: string): Promise<SyncNowResult> {
  const domain = 'changfa_favorite_addresses';
  const cfg = getSyncConfig();
  if (!cfg) return { ok: false, pulled: 0, pushed: 0, error: '未配置同步' };

  const def = DOMAIN_BY_KEY.get(`ls:${domain}`);
  if (!def) return { ok: false, pulled: 0, pushed: 0, error: '未知同步域' };

  const current = await readDomain(def);
  const favorite = Array.isArray(current)
    ? current.find((item) => item && typeof item === 'object' && String((item as { id?: unknown }).id) === favoriteId)
    : undefined;
  if (!favorite) return { ok: false, pulled: 0, pushed: 0, error: '本地未找到该收藏' };

  const localHash = hashOf(favorite);
  const stamp = Date.now() + clockOffset;

  let pushResp: { results?: Array<{ key: string; status: string }> } | null = null;
  try {
    pushResp = await syncPush(cfg, getDeviceId(), [{
      domain, id: String(favoriteId), data: favorite, deleted: false,
      updatedAt: stamp, deviceId: getDeviceId(),
    }]);
  } catch (e) {
    console.warn('[sync] 收藏地址直接强推失败:', e);
  }

  const accepted = pushResp != null && Array.isArray(pushResp.results) && pushResp.results.some(
    (r) => r.key === `${domain}/${String(favoriteId)}` && r.status === 'accepted',
  );

  // ---------- 服务端可见性自检（轻量端点，不拉整张表）----------
  let serverConfirmed = false;
  try {
    serverConfirmed = await hasRecordOnServer(domain, favoriteId, localHash);
  } catch { /* Best-effort operation; failure is non-fatal. */ }

  if (accepted && serverConfirmed) {
    // 服务端已确认：snap 更新（同步引擎下次 computeChanges 不会误判）
    if (localHash !== undefined) {
      state.snap[domain] ||= {};
      state.snap[domain][String(favoriteId)] = localHash;
    }
    return { ok: true, pulled: 0, pushed: 1 };
  }

  // ---------- 兜底：accepted 但服务端拿不到 / 或 push 直接失败 ----------
  //  #830 移除 flushSyncBeforeLogout 全量兜底（30s 等 idle 在所有手机上都会卡死），
  //  改为短超时再强推一次 + hasRecordOnServer 二次校验。
  //  仍失败就让用户立即看到错误，不再"转几分钟"。
  if (accepted || !accepted) { // 不论 push 成功或失败都走这条兜底
    try {
      const retryStamp = Date.now() + clockOffset;
      const retryResp = await syncPush(cfg, getDeviceId(), [{
        domain, id: String(favoriteId), data: favorite, deleted: false,
        updatedAt: retryStamp, deviceId: getDeviceId(),
      }]);
      const retryAccepted = Array.isArray(retryResp?.results) && retryResp.results.some(
        (r) => r.key === `${domain}/${String(favoriteId)}` && r.status === 'accepted',
      );
      serverConfirmed = retryAccepted ? await hasRecordOnServer(domain, favoriteId, localHash).catch(() => false) : false;
      if (serverConfirmed && localHash !== undefined) {
        state.snap[domain] ||= {};
        state.snap[domain][String(favoriteId)] = localHash;
      }
    } catch (e) {
      console.warn('[sync] 收藏地址兜底重推失败:', e);
      serverConfirmed = false;
    }
  }

  if (!serverConfirmed) {
    return {
      ok: false, pulled: 0, pushed: 1,
      error: '地址已保存在当前手机，但服务器尚未确认（请检查网络后再次点击保存）',
    };
  }
  return { ok: true, pulled: 0, pushed: 1 };
}

/** 服务端可见性：调轻量端点 /api/has-record 确认 (domain, id) 在服务端是否 alive。
 *  注意：早期实现走 syncPull(cursor=0) 会一次拉回全表 4 万条记录 → JSON 解析卡死主线程，
 *  表现为保存按钮一直转圈。这里改用专用端点，单次 ~1ms 返回。
 */
async function hasRecordOnServer(domain: string, id: string, expectedHash?: number): Promise<boolean> {
  const cfg = getSyncConfig();
  if (!cfg) return false;
  // #830 裸 fetch 是挂死的元凶——服务器 CPU 满时排队、JS 暂停期间不推进。iOS Safari / 安卓 Chrome 切到后台再回来，
  //  这个 fetch 可能永久挂着 → syncFavoriteAddressNow 看起来"卡几分钟"。加 5s AbortController 兜底。
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 5000);
  try {
    const r = await fetch(cfg.serverUrl.replace(/\/+$/, '') + '/api/has-record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${loadSession()?.token || ''}` },
      body: JSON.stringify({ domain, id: String(id), expectedHash }),
      signal: ctrl.signal,
    });
    if (!r.ok) return false;
    const j = await r.json() as { exists?: boolean; deleted?: boolean; matchesExpected?: boolean };
    return !!(j && j.exists && !j.deleted && (expectedHash === undefined || j.matchesExpected === true));
  } catch (e) {
    console.warn('[sync] hasRecordOnServer 失败:', e);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** 一轮完整同步：pull（循环分页）→ push（diff 增量）。任何一步失败即中止本轮。 */
export async function syncNow(reason = 'manual'): Promise<SyncNowResult> {
  const cfg = getSyncConfig();
  if (!cfg || !cfg.enabled) return { ok: false, pulled: 0, pushed: 0, error: '未启用同步' };
  if (syncing) {
    // 后台轮询/回前台检查不追队，避免网络较慢时永远处于“同步中”。
    if (shouldQueueBusySync(reason)) pendingAfterSync = true;
    return { ok: false, pulled: 0, pushed: 0, error: '同步进行中' };
  }
  if (encryptionLocked()) return { ok: false, pulled: 0, pushed: 0, error: '数据已加密锁定，解锁后再同步' };

  syncing = true;
  syncingSince = Date.now();
  lastError = '';
  emitStatus();
  let pulled = 0;
  let pushed = 0;
  let rejected: SyncRejectedRecord[] = [];
  const pulledDomains = new Set<string>();
  try {
    const deviceId = getDeviceId();

    // 1) 拉取（分页循环；拉取失败则本轮直接放弃——铁律）
    // #A7（2026-09-13）value 模式：告诉服务端哪些域只需拉最新 1 条整值 record,
    // 避免下发 4 万条 products 历史 record,拉取耗时从 ~60s 降到 < 1s。
    // #A12（2026-09-13）接收服务端 value-mode 全局接管 seq 映射，写入 state。
    const valueDomains = ALL_DOMAINS.filter((d) => d.mode === 'value').map((d) => d.domain);
    let resyncDone = false;
    for (;;) {
      const page = await syncPull(cfg, state.cursor, valueDomains);
      // #766：用服务器时间修正本机时钟偏差（后续生成/比较的时间戳全网统一锚点）
      if (typeof page.serverTime === 'number' && page.serverTime > 0) {
        clockOffset = page.serverTime - Date.now();
      }
      // #A12 把服务端回传的 value-mode 接管 seq 写入 state（computeChanges 据此跳过重复推送）
      if (page.valueTakeoverSeq) {
        for (const [d, seq] of Object.entries(page.valueTakeoverSeq)) {
          const cur = state.valueTakeoverSeq[d] || 0;
          if (typeof seq === 'number' && seq > cur) state.valueTakeoverSeq[d] = seq;
        }
      }
      // #A18（2026-09-14）全量重拉检查**前置到应用记录之前**。
      //  原实现放在 `!page.hasMore` 分支且要求 `state.cursor > 0`，导致重新登录
      //  （cursor 已归 0）的设备永远走不到清理逻辑，第一轮就被污染的 meta 过滤掉。
      if (typeof page.fullResyncVersion === 'number' && page.fullResyncVersion > 0 && !resyncDone) {
        const localV = Number(localStorage.getItem(FULL_RESYNC_KEY) || '0');
        if (localV !== page.fullResyncVersion) {
          resyncDone = true;
          try { localStorage.setItem(FULL_RESYNC_KEY, String(page.fullResyncVersion)); } catch { /* ignore */ }
          // meta =「已处理过的服务端 updatedAt」索引。若被脏数据污染成超大值
          //  （例：运维脚本用 now+1天 写库），后续正常的删除/修改会被
          //  `tsOf(r) > known` 永久过滤 → 客户端再也同步不了。全量重拉时清空它，
          //  让所有记录按服务端为准重新应用一遍。
          // ⚠️ 只清 meta，**不清 snap**——snap 用于计算本地未推送变更，
          //   清空会导致全量回推并可能覆盖他人在服务器上 newer 的数据。
          const metaCount = Object.keys(state.meta || {}).length;
          state.meta = {};
          console.warn(
            `[sync] #A15/#A18 全量重拉 v${localV} → v${page.fullResyncVersion}` +
            `（已清空本地 meta ${metaCount} 个域）`,
          );
          if (state.cursor > 0) {
            state.cursor = 0;
            continue; // 归零后重拉一轮
          }
          // cursor 本来就是 0 → 当前页即全量，清完 meta 直接往下应用
        }
        try { localStorage.setItem(FULL_RESYNC_KEY, String(page.fullResyncVersion)); } catch { /* ignore */ }
      }
      if (page.records.length > 0) {
        const sorted = [...page.records].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
        const res = await applyRemoteRecords(sorted);
        pulled += res.applied;
        for (const d of res.domains) pulledDomains.add(d);
        state.cursor = Math.max(state.cursor, sorted[sorted.length - 1]?.seq ?? 0);
      }
      if (!page.hasMore) {
        // 空页也推进 cursor 到服务器当前 seq，避免重复扫描
        if (page.records.length === 0) state.cursor = Math.max(state.cursor, page.seq);
        // #A15（2026-09-14）服务端可控强制全量：版本号的记录与「清 meta / 归零 cursor」
        //  已前置到应用记录之前（见上方 #A18），这里只兜底确保版本号被持久化。
        if (typeof page.fullResyncVersion === 'number' && page.fullResyncVersion > 0) {
          try { localStorage.setItem(FULL_RESYNC_KEY, String(page.fullResyncVersion)); } catch { /* ignore */ }
        }
        break;
      }
    }

    // #762：拉取应用了 products/suppliers 域后做 brand/supplierName 历史脏数据修复。
    // #828：改为 await，确保首屏渲染前本地已完成修复；随后 computeChanges 会把
    // 干净数据立即回推服务器，避免「渲染用脏数据→修复在后台才完成」的竞态。
    // 幂等，修完即无操作。
    // #824：供应商 category 误填品牌列表的修复走同一模式。
    if (pulledDomains.has('products') || pulledDomains.has('suppliers')) {
      await repairProductBrandSupplierNow();
    }
    if (pulledDomains.has('suppliers')) {
      await repairSupplierBrandCategoryNow();
    }

    // 2) 推送本地变更。账号刚切换时首轮只允许服务端覆盖本地缓存，禁止回推旧账号状态。
    if (skipPushOnce) {
      skipPushOnce = false;
    } else {
      const diff = await computeChanges(deviceId);
      if (diff.records.length > 0) {
        const pushResult = await pushAll(cfg, deviceId, diff.records);
        pushed = pushResult.accepted;
        rejected = pushResult.rejected;
      }
    }

    lastSyncAt = Date.now();
    writeTimes = {};
    await persistState();
    if (rejected.length > 0) {
      // 拒绝项不能继续留在 outbox 快照中。下一轮完整拉取只回填、不回推，
      // 让服务器真值覆盖本地旧值，避免跨账号长期不一致。
      for (const item of rejected) {
        delete state.snap[item.domain]?.[item.id];
        delete state.meta[item.domain]?.[item.id];
      }
      state.cursor = 0;
      skipPushOnce = true;
      await persistState();
      const reasons = [...new Set(rejected.map((item) => item.reason || item.status))].join('；');
      lastError = `${rejected.length} 条数据被服务器拒绝：${reasons}`;
      // 权限不足、陈旧版本等被拒绝时需要从服务器回填一次，但不能每次轮询都
      // 重置 cursor 并全量拉取。iPhone 上这种循环会让状态长期显示“同步中”。
      if (shouldRunRejectedReconcile(lastRejectedReconcileAt)) {
        lastRejectedReconcileAt = Date.now();
        setTimeout(() => void syncFullNow('server-rejection-reconcile'), 0);
      } else {
        lastError += '；已暂停重复全量拉取，将在 5 分钟后再尝试恢复';
      }
      return { ok: false, pulled, pushed, rejected, error: lastError };
    }
    return { ok: true, pulled, pushed, rejected };
  } catch (e) {
    lastError = e instanceof Error ? e.message : '同步失败';
    // 状态可能部分更新（如拉取成功推送失败），落盘保留进度
    try { await persistState(); } catch { /* Best-effort operation; failure is non-fatal. */ }
    console.warn(`[sync] 同步失败(${reason}):`, lastError);
    return { ok: false, pulled, pushed, error: lastError };
  } finally {
    syncing = false;
    emitStatus();
    if (pendingAfterSync) {
      pendingAfterSync = false;
      void syncNow(reason);
    }
  }
}

// ---------- 种子设备全量上传 ----------

/** 清空本地快照 → 下一轮 diff 视全部数据为新增 → 分批全量推送（电脑做种子设备时用） */
export async function bootstrapUpload(): Promise<SyncNowResult> {
  state.snap = {};
  writeTimes = {};
  await persistState();
  return syncNow('bootstrap');
}

/** 其他设备首次接入：从服务器全量拉取（cursor 归零重扫） */
export async function fullPull(): Promise<SyncNowResult> {
  state.cursor = 0;
  await persistState();
  return syncNow('full-pull');
}

// ---------- 触发器 ----------

const PUSH_DEBOUNCE_MS = 3000;
const INTERVAL_MS = SYNC_INTERVAL_MS;
const MIN_INTERVAL_BETWEEN_SYNC = 10 * 1000;

// #761 热键立即推送：这些域数据小、实时性要求高（收藏地址等「保存即走」场景——
// 手机保存后立刻锁屏/切走，3s 防抖来不及触发就丢到下一轮 15s 轮询）。
// 写入时清掉防抖定时器直接 syncNow；自身带 3s 限频，防止连续写入打爆服务器。
const HOT_PUSH_DOMAINS = new Set<string>(['changfa_favorite_addresses', 'changfa_store_location', 'changfa_driver_locations']);
const HOT_PUSH_MIN_INTERVAL_MS = 3 * 1000;
let lastHotPushAt = 0;

function scheduleDebouncedSync(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void syncNow('debounced-write');
  }, PUSH_DEBOUNCE_MS);
}

function notifyWrite(domain: string): void {
  if (applyingRemote) return;
  // #766：写入时刻记「服务器锚定时间」，保住离线编辑的先后关系
  writeTimes[domain] = Date.now() + clockOffset;
  if (HOT_PUSH_DOMAINS.has(domain) && Date.now() - lastHotPushAt >= HOT_PUSH_MIN_INTERVAL_MS) {
    lastHotPushAt = Date.now();
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    void syncNow('hot-write');
    return;
  }
  scheduleDebouncedSync();
}

/** 启动引擎：注册写入钩子 + 定时/在线/可见性触发。main.tsx 在 initMockData 后调用。 */
export async function startSyncEngine(): Promise<void> {
  if (started) return;
  started = true;

  // 恢复持久化状态（游标/快照/meta）
  const saved = await dbGet<SyncState>(STATE_KEY);
  if (saved && typeof saved.cursor === 'number') {
    state = {
      cursor: saved.cursor || 0,
      meta: saved.meta || {},
      snap: saved.snap || {},
      valueTakeoverSeq: saved.valueTakeoverSeq || {},
    };
  }

  // IndexedDB 写入钩子（db.ts 单点拦截全部核心域写入）
  onDbWrite((key) => {
    const d = DOMAIN_BY_KEY.get(`idb:${key}`);
    if (d) notifyWrite(d.domain);
  });

  // localStorage 拦截（白名单键）—— 仅拦截本应用写入，绕过引擎自身应用
  const origSet = Storage.prototype.setItem;
  const origRemove = Storage.prototype.removeItem;
  Storage.prototype.setItem = function patchedSetItem(k: string, v: string) {
    origSet.call(this, k, v);
    if (applyingRemote) return;
    const d = DOMAIN_BY_KEY.get(`ls:${k}`);
    if (d) notifyWrite(d.domain);
  };
  Storage.prototype.removeItem = function patchedRemoveItem(k: string) {
    origRemove.call(this, k);
    if (applyingRemote) return;
    const d = DOMAIN_BY_KEY.get(`ls:${k}`);
    if (d) notifyWrite(d.domain);
  };

  // 定时触发（页面生命周期常驻，无需停止）
  setInterval(() => {
    const cfg = getSyncConfig();
    if (cfg?.enabled && navigator.onLine) void syncNow('interval');
  }, INTERVAL_MS);

  // 网络恢复触发
  window.addEventListener('online', () => void syncNow('online'));

  // #761 回前台触发（限频）：iOS 微信 WKWebView 冻结后恢复只触发 pageshow/focus、
  // 不一定触发 visibilitychange —— 三个事件全挂，共用同一套「启用+联网+10s 限频」检查
  const syncIfForeground = (reason: string) => {
    const cfg = getSyncConfig();
    if (!cfg?.enabled) return;
    if (!navigator.onLine) return;
    // #831 syncing 看门狗：JS 暂停期间 fetch 永久挂着 + finally 不执行 → syncing=true 卡死。
    //  页面恢复可见时如果 syncing=true 已持续超过 90 秒，强制重置为 false，让下一轮 syncNow 能正常启动。
    if (syncing && Date.now() - syncingSince > 90_000) {
      console.warn(`[sync] 看门狗：syncing=true 已卡 ${Math.round((Date.now() - syncingSince) / 1000)}s，强制重置`);
      syncing = false;
      lastError = '上次同步因 JS 暂停超时未结束，已自动恢复';
      emitStatus();
    }
    if (Date.now() - lastSyncAt < MIN_INTERVAL_BETWEEN_SYNC) return;
    void syncNow(reason);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    syncIfForeground('visible');
  });
  // pageshow（bfcache/冻结恢复必触发）；focus 覆盖切后台后点回来的场景
  window.addEventListener('pageshow', () => syncIfForeground('pageshow'));
  window.addEventListener('focus', () => syncIfForeground('focus'));

  // 启动首轮
  //  #A8（2026-09-13）用户实测反馈：「每次同一台手机同一个浏览器都被全量拉一次」。
  //  根因：原代码无条件 `state.cursor = 0` 强制重置,即使 saved cursor > 0 也被抹掉。
  //  修复：只在新设备/本地被清过（cursor 缺失或为 0）才走 startup-full 全量拉；
  //  老用户走 startup-incremental 只拉增量（通常 0-10 条,< 1 秒）。
  //  兜底：若 incremental 发现服务端 seq < 本地 cursor（理论上不该发生），仍可走全量——暂不做。
  //  「手动全量同步」保留为单独的 triggerSyncFull()，给用户需要的时候调用。
  const cfg = getSyncConfig();
  if (cfg?.enabled) {
    const savedCursor = typeof state.cursor === 'number' ? state.cursor : 0;
    let integrityLost = false;
    if (savedCursor > 0) {
      // #A10（2026-09-14）数据丢失事故防护：iPhone Safari 在标签不活跃时会清 LS，
      //  cursor > 0 但 IDB 关键域都是空 → 走增量同步时 applyRemoteRecords 没东西覆盖，
      //  UI 看到 fallback MOCK 数据。强制校验：customers/salesOrders/products 三域全空
      //  → 认定为 LS 被清空（iOS Safari 标签冻结清理），强制走 cursor=0 全量拉。
      try {
        const [custs, sales, prods] = await Promise.all([
          dbGet<unknown[]>(KEYS.customers).catch(() => null),
          dbGet<unknown[]>(KEYS.salesOrders).catch(() => null),
          dbGet<unknown[]>(KEYS.products).catch(() => null),
        ]);
        const hasCustomers = Array.isArray(custs) && custs.length > 0;
        const hasSales = Array.isArray(sales) && sales.length > 0;
        const hasProducts = Array.isArray(prods) && prods.length > 0;
        if (!hasCustomers && !hasSales && !hasProducts) {
          console.warn('[sync] #A10 完整性校验失败：cursor > 0 但 IDB 三域全空，强制走 cursor=0 全量拉');
          integrityLost = true;
          state.cursor = 0;
          lastError = '检测到本地数据丢失（iPhone Safari 可能清理了存储），正在从云端恢复...';
          emitStatus();
        }
      } catch (e) {
        console.warn('[sync] #A10 完整性校验异常:', e);
      }
    }
    if (savedCursor > 0 && !integrityLost) {
      // 增量同步,不阻塞首屏:增量很快,正常情况拉到 0-20 条 records,< 1-2 秒
      //  iPhone Safari 上 history state.cursor 的恢复由 IndexedDB 兜底,30s 应该用不到
      await syncNow('startup-incremental');
    } else {
      // 真·新设备 或 #A10 完整性失败：cursor=0 走全量,仍 race 30s 不阻塞首屏
      state.cursor = 0;
      await Promise.race([
        syncNow(integrityLost ? 'startup-integrity-recovery' : 'startup-full'),
        new Promise((r) => setTimeout(r, 30000)),
      ]);
    }

    // #A7（2026-09-13）products 整值接管：首轮同步后,如果 products 域从未以整值模式
    //  推过（snap 里没有 `__value__` 键）、本地 IDB 又有 ≥50 条商品（说明这是从未走
    //  过整值模式的老设备），主动在 1.5s 后 trigger syncNow——computeChanges 走 value
    //  分支自动发 1 条 __value__ + N 条历史 tombstone，把服务端 sync_records.products
    //  从数千条瘦到 1 条，后续所有设备登录都快。
    // 距离上次接管 < 10 分钟则跳过（避免重复推送；首次接管后 snap 就会有 __value__，
    // 第二次来也直接早返）。
    void scheduleProductsTakeover();
  }
}

/**
 * products 整值主动接管调度。
 * - 条件：本地 IDB ≥ 50 条 且 snap.products 没有 __value__ 键
 * - 节流：本地记录上次接管时间，10 分钟内不重复
 * - 行为：调度 1.5s 后 syncNow('products-takeover')，让 computeChanges 自然切换整值
 */
async function scheduleProductsTakeover(): Promise<void> {
  try {
    const snapProducts = state.snap.products || {};
    if (snapProducts[VALUE_ID] !== undefined) return;
    const idbProducts = ((await dbGet<Product[]>(KEYS.products)) as Product[] | null) || [];
    if (!Array.isArray(idbProducts) || idbProducts.length < 50) return;
    const takeoverLast = ((await dbGet<number>('sync_products_takeover_v1')) as number | null) || 0;
    if (typeof takeoverLast === 'number' && takeoverLast > 0 && Date.now() - takeoverLast < 10 * 60_000) return;
    await dbSet('sync_products_takeover_v1', Date.now());
    console.info(`[sync] products 接管:本地 ${idbProducts.length} 条,snap 无 __value__,1.5s 后触发`);
    setTimeout(() => void syncNow('products-takeover'), 1500);
  } catch (e) {
    // Best-effort：接管检测失败不能让 syncEngine 起不来
    console.warn('[sync] products 接管检测失败（忽略）', e);
  }
}

/** 配置变更后由设置页调用：立即执行一轮（enabled 从 off→on 时等于首次接入） */
export function triggerSync(): void {
  void syncNow('config-change');
}

export async function syncFullNow(reason = 'manual-full'): Promise<SyncNowResult> {
  state.cursor = 0;
  return syncNow(reason);
}

/** 登录成功或手动恢复时从服务器重新扫描全部授权数据域。 */
export function triggerFullSync(): void {
  void syncFullNow('login-full');
}
